import { NavLink, useNavigate } from 'react-router-dom';
import { DisplayMarkdown } from '../ancillary/DisplayMarkdown';
import { BookingExtendedView, BookingExtendedViewRow } from 'cb-model';
import { formatEventUrl, formatSingleDate, getEventType } from './helpers';
import { OptionsField } from 'gossamer-unified';
import classes from './EventTile.module.css';
import { AttendeeCertificateDownloadButton } from './CertificateDownloadButton';
import { EventTileIcon } from './EventTileIcon';

export interface BookedEventTileProps {
  eventId: number;
  eventType: string;
  title: string;
  image?: string;
  plainDescription: string;
  markdownDescription?: string;
  startAt?: string; // ISO
  flowOption: string;
  ticketBookingId: number;
  attendanceResult?: string;
  hasLeftFeedback?: boolean;
}

export const BookedEventTile = (props: BookedEventTileProps) => {
  const navigate = useNavigate();

  const eventUrl = formatEventUrl(props.title || '', props.eventId);
  const truncatedDescription =
    props.plainDescription.length > 156 ? `${props.plainDescription.slice(0, 156)}...` : props.plainDescription;
  // Determine which action buttons to show based on flow_option
  const isPast = new Date(props.startAt || '') < new Date();
  const showAttendance = isPast && (props.flowOption === 'full' || props.flowOption === 'attendanceOnly');
  const showFeedback = isPast && (props.flowOption === 'full' || props.flowOption === 'noAttendance');
  const showCertificate = isPast && (props.flowOption === 'full' || props.flowOption === 'noAttendance');

  // Determine button states based on booking data
  const hasMarkedAttendance = !!props.attendanceResult;

  const hasAttended = props.attendanceResult === 'attended' || false;
  const markedNonAttendanceLabel =
    isPast && props.flowOption === 'none'
      ? 'not tracked'
      : (hasMarkedAttendance &&
          !hasAttended &&
          'marked ' +
            (BookingExtendedView.fields.attendance_result as OptionsField).options?.find(
              (opt) => opt.id === props.attendanceResult,
            )?.label) ||
        null;

  const activeAttendance = !hasMarkedAttendance;
  const activeFeedback =
    ((hasMarkedAttendance && hasAttended) || props.flowOption === 'noAttendance') && !props.hasLeftFeedback;
  const activeCertificate =
    ((hasMarkedAttendance && hasAttended) || props.flowOption === 'noAttendance') && props.hasLeftFeedback;

  const handleRecordAttendance = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/website/attend-sign/${props.ticketBookingId}`);
  };

  const handleLeaveFeedback = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/website/event-feedback/${props.eventId}`);
  };

  return (
    <div className={classes.EventTile}>
      <NavLink to={eventUrl}>
        <div
          className="image"
          style={props.image ? { backgroundImage: `url(${props.image})` } : { backgroundColor: '#f1f5f9' }}
        >
          <div className="eventType">{getEventType(props.eventType)}</div>
        </div>
        <div className="main">
          <h2>{props.title}</h2>
          <div className={classes.description}>
            {props.markdownDescription ? (
              <DisplayMarkdown source={props.markdownDescription} />
            ) : (
              <p>{truncatedDescription}</p>
            )}
          </div>
          <div className="bottom">
            <div className={classes.EventTileIcons}>
              {markedNonAttendanceLabel}
              {showAttendance && (
                <EventTileIcon
                  mode={activeAttendance ? 'active' : hasMarkedAttendance ? 'done' : 'inactive'}
                  imgSrc="/assets/icons/clipboard-document-check.svg"
                  onClick={handleRecordAttendance}
                  title={activeAttendance ? 'Record attendance' : 'Attendance already recorded'}
                />
              )}
              {showFeedback && (
                <EventTileIcon
                  mode={activeFeedback ? 'active' : props.hasLeftFeedback ? 'done' : 'inactive'}
                  imgSrc="/assets/icons/chat-bubble-left-right.svg"
                  onClick={handleLeaveFeedback}
                  title={
                    activeFeedback
                      ? 'Leave feedback'
                      : props.hasLeftFeedback
                        ? 'Feedback already submitted'
                        : 'Not available'
                  }
                />
              )}
              {showCertificate && (
                <AttendeeCertificateDownloadButton
                  disabled={!activeCertificate}
                  ticketBookingId={props.ticketBookingId}
                />
              )}
            </div>
            {props.startAt && <div className="startDate">{formatSingleDate(new Date(props.startAt), true)}</div>}
          </div>
        </div>
      </NavLink>
    </div>
  );
};

interface BookingEventTileFromRowProps {
  item: BookingExtendedViewRow;
}

export const BookingEventTileFromRow = ({ item }: BookingEventTileFromRowProps) => (
  <BookedEventTile
    key={item.event_id}
    eventId={item.event_id}
    eventType={getEventType(item.type)}
    title={item.title}
    image={item.images}
    plainDescription={item.plain_description}
    markdownDescription={item.markdown_description || undefined}
    startAt={item.start_at}
    flowOption={item.flow_option}
    ticketBookingId={item.ticket_booking_id}
    attendanceResult={item.attendance_result}
    hasLeftFeedback={!!item.event_feedback_id}
  />
);
